import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { getErrorMessage } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Loader2, CheckCircle2, XCircle, Mail } from "lucide-react";

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("verifying"); // verifying, success, error
  const [message, setMessage] = useState("");
  
  const token = searchParams.get("token");

  useEffect(() => {
    const verifyEmail = async () => {
      if (!token) {
        setStatus("error");
        setMessage("No verification token provided");
        return;
      }

      try {
        const response = await axios.get(`${API}/auth/verify-email?token=${token}`);
        setStatus("success");
        setMessage(response.data.message);
        toast.success("Email verified successfully!");
      } catch (error) {
        setStatus("error");
        const errorMessage = getErrorMessage(error.response?.data?.detail, "Verification failed");
        setMessage(errorMessage);
        toast.error(errorMessage);
      }
    };

    verifyEmail();
  }, [token]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background effects */}
      <div className="absolute inset-0 overflow-hidden">
        <div className={`absolute -top-1/2 -left-1/2 w-full h-full rounded-full blur-3xl transition-colors ${
          status === 'success' ? 'bg-gradient-to-br from-green-500/10 via-transparent to-transparent' :
          status === 'error' ? 'bg-gradient-to-br from-red-500/10 via-transparent to-transparent' :
          'bg-gradient-to-br from-primary/10 via-transparent to-transparent'
        }`} />
      </div>

      <Card className="w-full max-w-md glass-heavy animate-fade-in relative z-10" data-testid="verify-card">
        <CardHeader className="space-y-4 text-center">
          <div className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center ${
            status === 'success' ? 'bg-green-500/20' :
            status === 'error' ? 'bg-red-500/20' :
            'bg-primary/20'
          }`}>
            {status === 'verifying' && (
              <Loader2 className="h-8 w-8 text-primary animate-spin" />
            )}
            {status === 'success' && (
              <CheckCircle2 className="h-8 w-8 text-green-500" />
            )}
            {status === 'error' && (
              <XCircle className="h-8 w-8 text-red-500" />
            )}
          </div>
          
          <CardTitle className="text-2xl">
            {status === 'verifying' && 'Verifying Email...'}
            {status === 'success' && 'Email Verified!'}
            {status === 'error' && 'Verification Failed'}
          </CardTitle>
          
          <CardDescription>
            {message || (status === 'verifying' ? 'Please wait while we verify your email address...' : '')}
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-4">
          {status === 'success' && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground text-center">
                Your account is now active. You can log in and start creating campaigns.
              </p>
              <Button 
                className="w-full" 
                onClick={() => navigate('/login')}
                data-testid="login-after-verify-btn"
              >
                Continue to Login
              </Button>
            </div>
          )}
          
          {status === 'error' && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground text-center">
                The verification link may have expired or already been used.
              </p>
              <div className="flex flex-col gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => navigate('/login')}
                >
                  Back to Login
                </Button>
                <Button 
                  variant="ghost"
                  onClick={() => navigate('/register')}
                >
                  Create New Account
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
